import { useState } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { LifeBuoy, X, Loader2, CheckCircle2, AlertTriangle, Clock } from "lucide-react";

const STATUS_BADGE = {
  fixed: { cls: "bg-emerald-100 text-emerald-800", icon: CheckCircle2, label: "Fixed" },
  escalated: { cls: "bg-amber-100 text-amber-800", icon: AlertTriangle, label: "Escalated" },
  resolved: { cls: "bg-sky-100 text-sky-800", icon: CheckCircle2, label: "Resolved by admin" },
};

export const SupportWidget = () => {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [tickets, setTickets] = useState([]);
  const [lastResult, setLastResult] = useState(null);

  const loadTickets = async () => {
    try {
      const { data } = await api.get("/support/tickets/mine?limit=10");
      setTickets(data.tickets || []);
    } catch { /* ignore */ }
  };

  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (next) loadTickets();
  };

  const submit = async () => {
    if (text.trim().length < 5) { toast.error("Describe the issue in a few words."); return; }
    setSending(true);
    setLastResult(null);
    try {
      const { data } = await api.post("/support/tickets", { text: text.trim() });
      setLastResult(data);
      setText("");
      toast.success(data.status === "fixed" ? "Issue fixed automatically" : "Escalated to admin");
      loadTickets();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not submit the issue");
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <button
        data-testid="support-widget-btn"
        onClick={toggle}
        className="fixed bottom-5 right-5 z-[10000] h-12 w-12 rounded-full bg-primary text-primary-foreground shadow-lg flex items-center justify-center hover:opacity-90 transition-opacity"
        title="Report an issue"
      >
        {open ? <X size={18} /> : <LifeBuoy size={20} />}
      </button>
      {open && (
        <div className="fixed bottom-20 right-5 z-[10000] w-[360px] max-w-[calc(100vw-2rem)] border border-border rounded-sm bg-card shadow-xl" data-testid="support-widget-panel">
          <div className="px-4 py-3 border-b border-border">
            <div className="font-display text-sm font-semibold flex items-center gap-2"><LifeBuoy size={14} /> CRM Support Agent</div>
            <div className="text-[10px] text-muted-foreground">Describe the problem — I&apos;ll try to fix it automatically, or escalate to admin.</div>
          </div>
          <div className="p-4 space-y-2">
            <textarea
              data-testid="support-issue-input"
              value={text}
              onChange={(e) => setText(e.target.value)}
              rows={3}
              placeholder="e.g. My Leads page is blank / I'm locked out during my shift…"
              className="w-full text-xs border border-input rounded-sm p-2 bg-background resize-none"
            />
            <Button size="sm" className="rounded-sm w-full" onClick={submit} disabled={sending} data-testid="support-submit-btn">
              {sending ? (<><Loader2 size={12} className="animate-spin mr-1" /> Diagnosing &amp; fixing…</>) : "Submit issue"}
            </Button>
            {lastResult && (
              <div className={`text-[11px] p-2 rounded-sm border ${lastResult.status === "fixed" ? "bg-emerald-50 border-emerald-200 text-emerald-900" : "bg-amber-50 border-amber-200 text-amber-900"}`} data-testid="support-last-result">
                <div className="font-semibold mb-0.5">{lastResult.status === "fixed" ? "Fixed ✅" : "Escalated to admin ⚠️"}</div>
                {lastResult.diagnosis}
              </div>
            )}
          </div>
          {tickets.length > 0 && (
            <div className="border-t border-border max-h-48 overflow-y-auto" data-testid="support-my-tickets">
              <div className="px-4 pt-2 text-[10px] uppercase tracking-wider text-muted-foreground font-mono">My recent reports</div>
              <ul className="p-2 space-y-1">
                {tickets.map((t) => {
                  const b = STATUS_BADGE[t.status] || { cls: "bg-slate-100 text-slate-700", icon: Clock, label: t.status };
                  const Icon = b.icon;
                  return (
                    <li key={t.id} className="text-[11px] p-2 rounded-sm border border-border" data-testid={`support-ticket-${t.id}`}>
                      <div className="flex items-center justify-between gap-2 mb-0.5">
                        <span className={`inline-flex items-center gap-1 text-[9px] px-1.5 py-0.5 rounded-sm ${b.cls}`}><Icon size={9} /> {b.label}</span>
                        <span className="text-[9px] text-muted-foreground">{new Date(t.created_at).toLocaleString()}</span>
                      </div>
                      <div className="truncate text-muted-foreground">{t.text}</div>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default SupportWidget;
